import axios from 'axios';
import { env } from '@/lib/env';

/**
 * Base URL for agent-studio routes (core catalog, custom agents, usage stats).
 * Agent Studio lives on aistudioapi — prefer REACT_APP_VIBE_CODING_STUDIO_URL when set
 * so BACKEND_URL can point at authapi for Settings/MCP without breaking this page.
 * Prod gateway: base ends with /backend → /backend/api/agent-studio.
 * Local aistudio: base ends with /api → /api/agent-studio.
 */
export function getAgentStudioApiBase() {
  const studio =
    (env('REACT_APP_VIBE_CODING_STUDIO_URL') || env('REACT_APP_BACKEND_URL') || '').replace(
      /\/$/,
      '',
    );
  if (!studio) return '';

  if (studio.endsWith('/backend')) {
    return `${studio}/api/agent-studio`;
  }

  if (studio.endsWith('/api')) {
    return `${studio}/agent-studio`;
  }

  return `${studio}/api/agent-studio`;
}

const BASE = getAgentStudioApiBase();

export const AGENT_STUDIO_API = BASE;

export async function fetchCoreAgentCatalog() {
  const { data } = await axios.get(`${BASE}/core-agents`);
  return data;
}

export async function fetchAgentUsageStats(preset = 'last_30_days') {
  const { data } = await axios.get(`${BASE}/agent-usage`, { params: { preset } });
  return data;
}

export function formatTokenCount(value) {
  const n = Number(value) || 0;
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
  return n.toLocaleString();
}

export async function updateCoreAgentModel(agentId, modelId) {
  const { data } = await axios.put(`${BASE}/core-agents/${encodeURIComponent(agentId)}/model`, {
    model_id: modelId,
  });
  return data;
}

export async function resetCoreAgentModel(agentId) {
  const { data } = await axios.delete(`${BASE}/core-agents/${encodeURIComponent(agentId)}/model`);
  return data;
}

export function shortModelLabel(modelId) {
  if (!modelId) return 'Default';
  if (modelId.includes('opus-4-8')) return 'Claude Opus 4.8';
  if (modelId.includes('sonnet-4-5')) return 'Claude Sonnet 4.5';
  if (modelId.includes('sonnet-4-20250514')) return 'Claude Sonnet 4';
  const parts = String(modelId).split('.');
  return parts[parts.length - 1] || modelId;
}

export async function fetchAgentStudioMcpServers() {
  const { data } = await axios.get(`${BASE}/mcp-servers`);
  return data;
}

export async function updateCoreAgentMcpServers(agentId, mcpServerIds) {
  const { data } = await axios.put(
    `${BASE}/core-agents/${encodeURIComponent(agentId)}/mcp-servers`,
    { mcp_server_ids: mcpServerIds },
  );
  return data;
}

export async function resetCoreAgentMcpServers(agentId) {
  const { data } = await axios.delete(
    `${BASE}/core-agents/${encodeURIComponent(agentId)}/mcp-servers`,
  );
  return data;
}

export function mcpEnvDefaultLabel(token) {
  const key = String(token || '');
  if (key === 'env:security_mcp') return 'Security MCP (environment)';
  if (key === 'env:odata_mcp') return 'OData MCP (environment)';
  if (key === 'env:abap_mcp') return 'ABAP MCP (environment)';
  return key;
}

export function mcpServerLabel(serverId, mcpServers = []) {
  const id = String(serverId || '');
  if (id.startsWith('env:')) return mcpEnvDefaultLabel(id);
  const row = (mcpServers || []).find((s) => s.id === id);
  return row?.display_name || id;
}

/** Human-readable summary of selected MCP bindings (registered servers only). */
export function formatMcpSelectionSummary(serverIds, mcpServers = []) {
  const ids = (Array.isArray(serverIds) ? serverIds : [])
    .filter(Boolean)
    .filter((id) => !String(id).startsWith('env:'));
  if (ids.length) {
    return ids.map((id) => mcpServerLabel(id, mcpServers)).join(', ');
  }
  return 'No MCP servers selected';
}

export function initialMcpDraft(configuredIds, effectiveIds) {
  if (Array.isArray(configuredIds)) {
    return configuredIds.filter((id) => id && !String(id).startsWith('env:'));
  }
  return (effectiveIds || []).filter((id) => id && !String(id).startsWith('env:'));
}

/** Normalize list responses from agent-studio / catalog. */
export function normalizeMcpServerList(payload) {
  if (Array.isArray(payload)) return payload;
  return payload?.servers || [];
}
